import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { ContentGrid } from "@/components/content/ContentGrid";
import { ContentItem } from "@/types/content";
import { getContentTypeFromUrl } from "@/utils/getContentTypeFromUrl";
import { useImportExploreContent } from "@/hooks/useImportExploreContent";
import { useTranslation } from "@/context/LanguageContext";


const INITIAL_VISIBLE = 6;

type ExploreCategory = {
  id: string;
  name: string;
  slug: string | null;
  accentColor: string | null;
};

type ExploreItem = {
  id: string;
  title: string;
  description: string | null;
  author: string | null;
  coverUrl: string | null;
  accentColor: string | null;
  categoryId: string | null;
  url: string | null;
  isFeatured: boolean;
};

export const ExploreSection: React.FC = () => {
  const { t } = useTranslation();
  const [categories, setCategories] = useState<ExploreCategory[]>([]);
  const [items, setItems] = useState<ExploreItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>("for-you");
  const [showAll, setShowAll] = useState<boolean>(false);
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const { importContent, importing, importingId } = useImportExploreContent();

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      setIsError(false);
      setErrorMessage(undefined);

      const [categoriesRes, itemsRes] = await Promise.all([
        supabase
          .from("explore_content_categories")
          .select("id, name, slug, accent_color")
          .order("order_index", { ascending: true }),
        supabase
          .from("explore_content")
          .select("id, title, description, author, cover_url, accent_color, category_id, url, is_featured")
          .order("order_index", { ascending: true })
          .limit(50),
      ]);

      if (categoriesRes.error) throw categoriesRes.error;
      if (itemsRes.error) throw itemsRes.error;

      const mappedCategories: ExploreCategory[] =
        categoriesRes.data?.map((c: any) => ({
          id: String(c.id),
          name: c.name,
          slug: c.slug ?? null,
          accentColor: c.accent_color ?? null,
        })) ?? [];

      const mappedItems: ExploreItem[] =
        itemsRes.data?.map((item: any) => ({
          id: String(item.id),
          title: item.title,
          description: item.description ?? null,
          author: item.author ?? null,
          coverUrl: item.cover_url ?? null,
          accentColor: item.accent_color ?? null,
          categoryId: item.category_id ? String(item.category_id) : null,
          url: item.url ?? null,
          isFeatured: item.is_featured ?? false,
        })) ?? [];

      setCategories(mappedCategories);
      setItems(mappedItems);
    } catch (err: any) {
      console.error("[ExploreSection] fetch error:", err);
      setIsError(true);
      setErrorMessage(err?.message ?? "Failed to load explore content.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const updateScrollButtons = useCallback(() => {
    if (!scrollContainerRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
  }, []);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    
    updateScrollButtons();
    container.addEventListener("scroll", updateScrollButtons);
    window.addEventListener("resize", updateScrollButtons);
    
    return () => {
      container.removeEventListener("scroll", updateScrollButtons);
      window.removeEventListener("resize", updateScrollButtons);
    };
  }, [updateScrollButtons, categories]);

  const handleScrollLeft = () => {
    if (!scrollContainerRef.current) return;
    scrollContainerRef.current.scrollBy({
      left: -200,
      behavior: "smooth"
    });
  };

  const handleScrollRight = () => {
    if (!scrollContainerRef.current) return;
    scrollContainerRef.current.scrollBy({
      left: 200,
      behavior: "smooth"
    });
  };

  const filteredItems = useMemo(() => {
    if (selectedCategoryId === "for-you") {
      return items;
    }
    return items.filter(item => item.categoryId && item.categoryId === selectedCategoryId);
  }, [items, selectedCategoryId]);

  const visibleItems = useMemo(() => {
    if (showAll) return filteredItems;
    return filteredItems.slice(0, INITIAL_VISIBLE);
  }, [filteredItems, showAll]);

  // Map explore items to ContentItem format for ContentGrid
  const contentItems: ContentItem[] = useMemo(() => {
    return visibleItems.map(item => ({
      id: item.id,
      title: item.title,
      type: getContentTypeFromUrl(item.url), // Infer type from URL extension
      thumbnailUrl: item.coverUrl,
      sourceUrl: item.url,
      createdAt: new Date().toISOString(), // Explore items don't have createdAt
      visibility: "public" as const,
    }));
  }, [visibleItems]);

  const hasMore = filteredItems.length > INITIAL_VISIBLE;

  // Build custom href for explore items - fallback URL
  const buildExploreHref = (contentItem: ContentItem) => {
    const exploreItem = visibleItems.find(i => i.id === contentItem.id);
    return exploreItem?.url ?? `/explore/${contentItem.id}`;
  };

  // Handle explore card click - import content to user's library
  const handleExploreCardClick = (contentItem: ContentItem) => {
    if (importing) return;
    const exploreItem = visibleItems.find(i => i.id === contentItem.id);
    if (exploreItem) {
      importContent({
        id: exploreItem.id,
        title: exploreItem.title,
        url: exploreItem.url,
        coverUrl: exploreItem.coverUrl,
      });
    }
  };

  return (
    <section className="w-full mb-10" data-explore-section="true">
      {/* Header */}
      <div className="mb-6">
        <div className="w-full flex justify-between items-center mb-4">
          <div className="flex items-center gap-2">
            <span className="text-base lg:text-lg font-semibold text-foreground">{t("dashboard.explore")}</span>
            <button
              type="button"
              onClick={async () => {
                setIsRefreshing(true);
                await fetchData();
                setIsRefreshing(false);
              }}
              disabled={isRefreshing || isLoading}
              className="inline-flex items-center justify-center p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors disabled:opacity-50"
              aria-label="Refresh explore"
            >
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
          <a
            href="/explore"
            className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          >
            {t("dashboard.viewAll")}
          </a>
        </div>

        {/* Category tabs with horizontal scroll */}
        <div className="w-full relative group/close mb-4">
          {/* Scroll-left button - only show when can scroll left */}
          {canScrollLeft && (
            <button
              type="button"
              onClick={handleScrollLeft}
              className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background focus-visible:outline-none hover:text-accent-foreground h-10 w-10 absolute left-1 top-1/2 z-[7] -mt-1 -translate-y-1/2 rounded-full border border-border bg-background/70 shadow-sm backdrop-blur transition-all hover:bg-background/80 opacity-0 group-hover/close:opacity-100"
              aria-label="Scroll left"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
          )}

          {/* Scroll-right button - only show when can scroll right */}
          {canScrollRight && (
            <button
              type="button"
              onClick={handleScrollRight}
              className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background focus-visible:outline-none hover:text-accent-foreground h-10 w-10 absolute right-1 top-1/2 z-[7] -mt-1 -translate-y-1/2 rounded-full border border-border bg-background/70 shadow-sm backdrop-blur transition-all hover:bg-background/80 opacity-0 group-hover/close:opacity-100"
              aria-label="Scroll right"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          )}

          {/* Scroll container */}
          <div className="w-full overflow-x-auto scrollbar-hide" ref={scrollContainerRef}>
            <div className="flex gap-2 pb-2 px-0.5 min-w-max">
              {/* For You tab */}
              <button
                type="button"
                onClick={() => setSelectedCategoryId("for-you")}
                className={[
                  "inline-flex items-center justify-center text-sm ring-offset-background focus-visible:outline-none h-9 rounded-lg px-3 group mt-0.5 flex-shrink-0 whitespace-nowrap font-medium shadow-[0_4px_10px_rgba(0,0,0,0.02)] transition-all duration-200",
                  selectedCategoryId === "for-you"
                    ? "border border-primary bg-primary text-primary-foreground ring-1 ring-primary/20 hover:bg-primary/90"
                    : "dark:bg-neutral-800/50 border border-primary/10 bg-white text-primary/90 hover:border-primary/20 hover:bg-white hover:text-primary dark:border-primary/5 dark:hover:border-primary/10"
                ].join(" ")}
              >
                {t("dashboard.forYou")}
              </button>

              {/* Dynamic category tabs */}
              {categories.map(category => {
                const isActive = selectedCategoryId === category.id;
                return (
                  <button
                    key={category.id}
                    type="button"
                    onClick={() => setSelectedCategoryId(category.id)}
                    className={[
                      "inline-flex items-center justify-center text-sm ring-offset-background focus-visible:outline-none h-9 rounded-lg px-3 group mt-0.5 flex-shrink-0 whitespace-nowrap font-medium shadow-[0_4px_10px_rgba(0,0,0,0.02)] transition-all duration-200",
                      isActive
                        ? "border border-primary bg-primary text-primary-foreground ring-1 ring-primary/20 hover:bg-primary/90"
                        : "dark:bg-neutral-800/50 border border-primary/10 bg-white text-primary/90 hover:border-primary/20 hover:bg-white hover:text-primary dark:border-primary/5 dark:hover:border-primary/10"
                    ].join(" ")}
                  >
                    <span className="truncate">{category.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      {isError ? (
        <div className="text-sm text-muted-foreground bg-muted/5 border border-muted/20 rounded-lg px-3 py-2">
          {errorMessage ?? "Something went wrong while loading content."}
        </div>
      ) : filteredItems.length === 0 && !isLoading ? (
        <div className="text-sm text-muted-foreground">
          {t("dashboard.noContentInCategory")}
        </div>
      ) : (
        <>
          {/* Content grid using ContentGrid component */}
          <ContentGrid
            items={contentItems}
            isLoading={isLoading}
            skeletonCount={INITIAL_VISIBLE}
            buildHref={buildExploreHref}
            onCardClick={handleExploreCardClick}
            loadingItemId={importingId}
          />

          {/* Show More / Show Less */}
          {hasMore && !isLoading && (
            <div className="flex justify-center mt-6">
              <button
                type="button"
                onClick={() => setShowAll(prev => !prev)}
                className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none bg-transparent h-9 rounded-lg px-4 gap-2 text-primary hover:text-primary/80"
              >
                <span>{showAll ? t("dashboard.showLess") : t("dashboard.showMore")}</span>
                <ChevronRight className={`h-4 w-4 transition-transform ${showAll ? "-rotate-90" : "rotate-90"}`} />
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
};
